import { z } from "zod";

import { ExtractAtomicOperationFromDefinition } from "../../atomic-operations/AtomicOperationDefinition.js";
import { DateTimeString, zDateTimeString } from "../../dateTypes.js";
import {
  AgentChatThreadId,
  CellId,
  DataConnectionId,
  HexId,
  MagicEventId,
  UserId,
} from "../../idTypeBrands.js";
import {
  ExploreIntent,
  MagicAcceptType,
  MagicCellPayload,
  MagicCellRunResult,
  MagicClientReferenceInfo,
  MagicEventFailureReason,
  MagicEventSource,
  MagicEventStatus,
  MagicKeyword,
  MagicRequestMode,
  MentionedDataframeName,
  zMentionedDataframeName,
} from "../../MagicTypes.js";
import { RichTextDocument } from "../../richTextTypes.js";
import { SemanticCap } from "../../semanticCaps.js";
import { createHexVersionAtomicOperationDefinition } from "../HexVersionAtomicOperationDefinition.js";
import {
  zAgentChatThreadId,
  zCellId,
  zDataConnectionId,
  zHexId,
  zMagicEventId,
  zUserId,
} from "../../zod/ZodIdTypeBrands.js";
import { zFromRuntype } from "../../zod/utils.js";

export interface MagicEventPayload {
  id: MagicEventId;
  cellId: CellId | null;
  accepted: boolean | null;
  agentChatThreadId: AgentChatThreadId | null;
  createdDate: DateTimeString;
  dataConnectionId: DataConnectionId | null;
  eventSource: MagicEventSource | null;
  eventType: MagicKeyword;
  result: string | null;
  status: MagicEventStatus | null;
  userId: UserId;
  userProvidedPrompt: string | null;
  parentEventId: MagicEventId | null;
  unsupportedResponse: boolean;
  prevEventId: MagicEventId | null;
  nextEventId: MagicEventId | null;
  revision: number;
  requestMode: MagicRequestMode | null;
  cellRunResult: MagicCellRunResult | null;
  acceptType: MagicAcceptType | null;
  userFacingResult: string | null;
  richTextPrompt: RichTextDocument | null;
  mentionedDataframes: MentionedDataframeName[] | null;
  failureReason: MagicEventFailureReason | null;
  autoTriggered: boolean;
  duration: number | null;
  timeToFirstToken: number | null;
  cellPayload: MagicCellPayload | null;
  relatedProjectIds: HexId[] | null;
  topReferenceInfo: MagicClientReferenceInfo[] | null;
  summary: string | null;
  intent: ExploreIntent | null;
  cancelled: boolean;
}

const CREATE_MAGIC_EVENT_TYPE = "CREATE_MAGIC_EVENT" as const;
export const CreateMagicEventPayload = z.object({
  id: zMagicEventId,
  cellId: zCellId.nullable(),
  accepted: z.boolean().nullable(),
  agentChatThreadId: zAgentChatThreadId.nullable(),
  createdDate: zDateTimeString,
  dataConnectionId: zDataConnectionId.nullable(),
  eventSource: z.nativeEnum(MagicEventSource).nullable(),
  eventType: z.nativeEnum(MagicKeyword),
  result: z.string().nullable(),
  status: z.nativeEnum(MagicEventStatus).nullable(),
  userId: zUserId,
  userProvidedPrompt: z.string().nullable(),
  parentEventId: zMagicEventId.nullable(),
  unsupportedResponse: z.boolean(),
  prevEventId: zMagicEventId.nullable(),
  nextEventId: zMagicEventId.nullable(),
  revision: z.number(),
  requestMode: z.nativeEnum(MagicRequestMode).nullable(),
  cellRunResult: z.nativeEnum(MagicCellRunResult).nullable(),
  acceptType: z.nativeEnum(MagicAcceptType).nullable(),
  userFacingResult: z.string().nullable(),
  richTextPrompt: zFromRuntype(RichTextDocument).nullable(),
  mentionedDataframes: z.array(zMentionedDataframeName).nullable(),
  failureReason: z.nativeEnum(MagicEventFailureReason).nullable(),
  autoTriggered: z.boolean(),
  duration: z.number().nullable(),
  timeToFirstToken: z.number().nullable(),
  cellPayload: zFromRuntype(MagicCellPayload).nullable(),
  relatedProjectIds: z.array(zHexId).nullable(),
  topReferenceInfo: z.array(zFromRuntype(MagicClientReferenceInfo)).nullable(),
  summary: z.string().nullable(),
  intent: z.nativeEnum(ExploreIntent).nullable(),
  cancelled: z.boolean(),
} satisfies Record<keyof MagicEventPayload, z.ZodTypeAny>);
export type CreateMagicEventPayload = z.infer<typeof CreateMagicEventPayload>;

export const CREATE_MAGIC_EVENT = createHexVersionAtomicOperationDefinition({
  type: CREATE_MAGIC_EVENT_TYPE,
  payloadSchema: CreateMagicEventPayload,
  readAuth: {
    kind: "hasSemanticCap",
    cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
  },
  writeAuth: {
    kind: "or",
    // If there is not cell already created, defualt to if the user has project
    // edit access
    or: [
      {
        kind: "and",
        and: [
          {
            kind: "hasSemanticCap",
            cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          },
          {
            kind: "idArgDoesNotExist",
            idArg: "cellId",
            idType: "Cell",
          },
        ],
      },
      // If there is a cell created that we're creating a magic event on the
      // user must have access to the underlying cell to be able to create the
      // magic event linked to that cell. This is important for cases like
      // Components where the user might not have access to the underlying cell.
      {
        kind: "and",
        and: [
          {
            kind: "hasSemanticCap",
            cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          },
          {
            kind: "hasSemanticCapOnIdArg",
            cap: SemanticCap.EDIT_PROJECT_CONTENTS,
            idArg: "cellId",
            idType: "Cell",
          },
        ],
      },
      {
        kind: "and",
        and: [
          {
            kind: "hasSemanticCap",
            cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          },
          {
            kind: "argIs",
            arg: "cellId",
            expectedValue: null,
          },
        ],
      },
    ],
  },
  scopedIds: {
    cellId: {
      scope: "ownedByWrapper",
      idType: "Cell",
      allowNonExistent: true,
    },
    // The event this operation creates. `writeAuth` does not assert the id is
    // unused, so `createdHere` would let a caller reuse an existing event's id
    // from another project. Requiring membership and tolerating the
    // not-yet-created case covers both.
    id: {
      scope: "ownedByWrapper",
      idType: "MagicEvent",
      allowNonExistent: true,
    },
    dataConnectionId: {
      scope: "external",
      reason:
        "Data connections are workspace-scoped and deliberately shared across projects.",
    },
    userId: {
      scope: "external",
      reason: "A user belongs to the workspace, not to any project.",
    },
    agentChatThreadId: {
      scope: "ownedByWrapper",
      idType: "AgentChatThread",
    },
    parentEventId: {
      scope: "ownedByWrapper",
      idType: "MagicEvent",
    },
    prevEventId: {
      scope: "ownedByWrapper",
      idType: "MagicEvent",
    },
    nextEventId: {
      scope: "ownedByWrapper",
      idType: "MagicEvent",
    },
    relatedProjectIds: {
      scope: "external",
      reason: "A hexVersion belongs to a Hex, not the other way round.",
    },
  },
  logSafe: [
    "id",
    "cellId",
    "accepted",
    "agentChatThreadId",
    "createdDate",
    "dataConnectionId",
    "eventSource",
    "eventType",
    "status",
    "userId",
    "parentEventId",
    "unsupportedResponse",
    "prevEventId",
    "nextEventId",
    "revision",
    "requestMode",
    "cellRunResult",
    "failureReason",
    "autoTriggered",
    "duration",
    "intent",
    "timeToFirstToken",
  ],
  conflictId: (op) => `${CREATE_MAGIC_EVENT_TYPE}-${op.payload.id}`,
  excludeFromHistory: true,
  create: (args: MagicEventPayload) => ({
    type: CREATE_MAGIC_EVENT_TYPE,
    payload: {
      ...args,
    },
  }),
});

export type CREATE_MAGIC_EVENT = ExtractAtomicOperationFromDefinition<
  typeof CREATE_MAGIC_EVENT
>;
